import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, ArrowLeft, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';

import { api, ErroApi } from '@/lib/api';
import {
  calcularIdade,
  mascararCns,
  mascararCpf,
  mascararTelefone,
  paraAsciiMaiusculo,
} from '@/lib/mascaras';
import {
  esquemaSolicitacao,
  hojeIso,
  prepararEdicao,
  valoresDoRegistro,
  type FormularioSolicitacao,
} from '@/features/solicitacoes/esquema';
import {
  DESCRICOES_PRIORIDADE,
  PRIORIDADES,
  ROTULOS_PRIORIDADE,
  ROTULOS_TIPO,
  TIPOS_SOLICITACAO,
} from '@/features/solicitacoes/status';
import {
  explicarSemEdicao,
  explicarSemEdicaoPerfil,
  podeEditar,
  podeEditarPerfil,
} from '@/features/solicitacoes/edicao';
import {
  caminhoDaLista,
  ehDemandaReprimida,
  type OrigemSolicitacao,
} from '@/features/solicitacoes/origem';
import type { SolicitacaoDetalhe } from '@/features/solicitacoes/tipos';
import type { Especialidade, UbsResumida } from '@/features/admin/tipos';
import { useAuth } from '@/features/auth/contexto';
import { CabecalhoPagina } from '@/components/cabecalho-pagina';
import { StatusBadge } from '@/components/status-badge';
import { Botao } from '@/components/ui/button';
import { AreaTexto, Entrada, Selecao } from '@/components/ui/input';
import { Campo } from '@/components/ui/campo';
import { Cartao, CartaoCabecalho, CartaoConteudo, CartaoTitulo } from '@/components/ui/card';
import { Esqueleto } from '@/components/ui/skeleton';

interface RespostaEdicao {
  alterada: boolean;
  camposAlterados: string[];
  alteracoes?: { rotulo: string; de: string; para: string }[];
  /** A correção acendeu o aviso de reavaliação para a Regulação. */
  regulacaoAvisada?: boolean;
}

/**
 * Correção dos dados de uma solicitação já registrada.
 *
 * Uma página, e não um diálogo: são treze campos com máscara e validação, e no
 * celular um formulário desse tamanho dentro de uma caixa flutuante fica com
 * metade dos campos atrás do teclado. Também não é um assistente de etapas
 * como o registro — quem abre esta tela já sabe qual campo veio errado e não
 * deve ter de atravessar duas etapas para chegar nele.
 */
export function PaginaEditarSolicitacao() {
  const { id } = useParams<{ id: string }>();
  const { usuario } = useAuth();
  const navegar = useNavigate();
  const queryClient = useQueryClient();

  const {
    data: solicitacao,
    isPending,
    error,
  } = useQuery({
    queryKey: ['solicitacao', id],
    queryFn: () => api.get<SolicitacaoDetalhe>(`/solicitacoes/${id}`),
    enabled: Boolean(id),
  });

  const formulario = useForm<FormularioSolicitacao>({
    resolver: zodResolver(esquemaSolicitacao),
    mode: 'onBlur',
  });

  // O formulário só existe depois que a solicitação chega; `reset` é o que
  // troca os valores padrão sem recriar o formulário a cada renderização.
  const { reset } = formulario;
  React.useEffect(() => {
    if (solicitacao) reset(valoresDoRegistro(solicitacao));
  }, [solicitacao, reset]);

  const { data: especialidades } = useQuery({
    queryKey: ['especialidades-ativas'],
    queryFn: () => api.get<Pick<Especialidade, 'id' | 'nome'>[]>('/especialidades/ativas'),
    staleTime: 5 * 60_000,
  });

  // Mesma chave da listagem e dos relatórios: normalmente já vem do cache.
  const { data: unidades } = useQuery({
    queryKey: ['ubs-ativas'],
    queryFn: () => api.get<UbsResumida[]>('/ubs/ativas'),
    staleTime: 5 * 60_000,
    enabled: solicitacao ? ehDemandaReprimida(solicitacao.origem) : false,
  });

  const salvar = useMutation({
    mutationFn: (dados: FormularioSolicitacao) =>
      api.patch<RespostaEdicao>(
        `/solicitacoes/${id}`,
        prepararEdicao(dados, solicitacao?.origem ?? 'UBS'),
      ),
    onSuccess: async (resultado) => {
      if (!resultado.alterada) {
        toast.info('Nenhum dado foi alterado.');
        await navegar(`/solicitacoes/${id}`);
        return;
      }

      // A quantidade de campos é a confirmação de que a correção pegou o que
      // se pretendia — "salvo com sucesso" não distingue um campo de treze. E
      // quem corrige precisa saber que a Regulação vai reavaliar: sem isso,
      // ligaria para avisar por telefone o que o sistema já avisou.
      const quantidade = resultado.camposAlterados.length;
      const oQue =
        quantidade === 1
          ? `${resultado.alteracoes?.[0]?.rotulo ?? 'Um campo'} atualizado.`
          : `${quantidade} campos atualizados.`;

      toast.success(
        resultado.regulacaoAvisada ? `${oQue} A Regulação será avisada da correção.` : oQue,
      );

      await queryClient.invalidateQueries({ queryKey: ['solicitacao', id] });
      await queryClient.invalidateQueries({ queryKey: ['historico', id] });
      await queryClient.invalidateQueries({ queryKey: ['solicitacoes'] });
      await queryClient.invalidateQueries({ queryKey: ['estatisticas'] });
      await navegar(`/solicitacoes/${id}`);
    },
    onError: (erro) => {
      toast.error(
        erro instanceof ErroApi ? erro.mensagemUsuario : 'Não foi possível salvar as alterações.',
      );
    },
  });

  if (isPending) {
    return (
      <div className="mx-auto flex max-w-2xl flex-col gap-4">
        <Esqueleto className="h-8 w-full max-w-64" />
        <Esqueleto className="h-96 w-full" />
      </div>
    );
  }

  if (error) {
    const naoEncontrada = error instanceof ErroApi && error.status === 404;

    return (
      <Impedimento
        titulo={naoEncontrada ? 'Solicitação não encontrada' : 'Não foi possível carregar'}
        detalhe={
          naoEncontrada
            ? 'O protocolo não existe ou pertence a outra unidade.'
            : error instanceof ErroApi
              ? error.mensagemUsuario
              : 'Tente novamente em instantes.'
        }
      />
    );
  }

  // A checagem real é do backend; esta existe para quem chega pela URL não
  // preencher um formulário inteiro e só então descobrir que não pode salvar.
  if (!podeEditar(solicitacao.status)) {
    return (
      <Impedimento
        titulo="Esta solicitação não pode ser editada"
        detalhe={explicarSemEdicao(solicitacao.status, solicitacao.agendamentoOrigem)}
        idSolicitacao={solicitacao.id}
        origem={solicitacao.origem}
      />
    );
  }

  if (usuario && !podeEditarPerfil(usuario.perfil, solicitacao.origem)) {
    return (
      <Impedimento
        titulo={
          ehDemandaReprimida(solicitacao.origem)
            ? 'Esta demanda reprimida é corrigida pela Regulação'
            : 'Seu perfil não corrige dados de solicitação'
        }
        detalhe={explicarSemEdicaoPerfil(usuario.perfil, solicitacao.origem)}
        idSolicitacao={solicitacao.id}
        origem={solicitacao.origem}
      />
    );
  }

  const valores = formulario.watch();
  const idade = valores.pacienteDataNascimento
    ? calcularIdade(valores.pacienteDataNascimento)
    : null;

  // A especialidade gravada pode ter sido inativada depois do registro. Sem
  // isto o `select` abriria em branco e um "salvar" acidental trocaria a
  // especialidade do pedido por outra qualquer.
  const opcoesEspecialidade = [...(especialidades ?? [])];
  if (!opcoesEspecialidade.some((item) => item.id === solicitacao.especialidade.id)) {
    opcoesEspecialidade.unshift(solicitacao.especialidade);
  }

  // Mesmo cuidado com a unidade: inativada depois do registro, ela sumiria da
  // lista e um "salvar" acidental moveria a demanda para outra UBS.
  const opcoesUbs = [...(unidades ?? [])];
  if (!opcoesUbs.some((item) => item.id === solicitacao.ubs.id)) {
    opcoesUbs.unshift({ id: solicitacao.ubs.id, nome: solicitacao.ubs.nome });
  }

  return (
    <div className="mx-auto max-w-2xl">
      <Botao variante="fantasma" tamanho="sm" asChild className="-ml-3 mb-2">
        <Link to={`/solicitacoes/${solicitacao.id}`}>
          <ArrowLeft aria-hidden="true" />
          Voltar à solicitação
        </Link>
      </Botao>

      <CabecalhoPagina
        titulo={`Editar ${solicitacao.protocolo}`}
        descricao="Correção dos dados do pedido. Cada alteração fica registrada no histórico, com o valor anterior."
        acoes={<StatusBadge status={solicitacao.status} />}
      />

      <div className="border-status-enviado-borda bg-status-enviado-fundo text-status-enviado-texto mb-5 flex items-start gap-2 rounded-md border p-3 text-sm">
        <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        <span>
          A edição não muda a situação da solicitação nem o que a Regulação já decidiu. Ela corrige
          o que o pedido diz — telefone que mudou, especialidade divergente da requisição do médico.
        </span>
      </div>

      <form
        onSubmit={(evento) => {
          evento.preventDefault();
          void formulario.handleSubmit((dados) => salvar.mutate(dados))();
        }}
        className="flex flex-col gap-5"
      >
        <Cartao>
          <CartaoCabecalho>
            <CartaoTitulo>Paciente</CartaoTitulo>
          </CartaoCabecalho>
          <CartaoConteudo className="flex flex-col gap-4">
            <Campo
              id="pacienteNome"
              rotulo="Nome completo"
              obrigatorio
              ajuda="Gravado em caixa alta e sem acentuação, para conferir com o eSUS."
              erro={formulario.formState.errors.pacienteNome?.message}
            >
              <Entrada
                autoComplete="off"
                {...formulario.register('pacienteNome', {
                  onChange: (evento: React.ChangeEvent<HTMLInputElement>) => {
                    formulario.setValue('pacienteNome', paraAsciiMaiusculo(evento.target.value));
                  },
                })}
              />
            </Campo>

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                id="pacienteDataNascimento"
                rotulo="Data de nascimento"
                obrigatorio
                ajuda={idade !== null ? `${idade} anos` : undefined}
                erro={formulario.formState.errors.pacienteDataNascimento?.message}
              >
                <Entrada type="date" {...formulario.register('pacienteDataNascimento')} />
              </Campo>

              <Campo
                id="pacienteNomeMae"
                rotulo="Nome da mãe"
                ajuda="Ajuda a Regulação a conferir a identidade."
              >
                <Entrada
                  autoComplete="off"
                  {...formulario.register('pacienteNomeMae', {
                    onChange: (evento: React.ChangeEvent<HTMLInputElement>) => {
                      formulario.setValue(
                        'pacienteNomeMae',
                        paraAsciiMaiusculo(evento.target.value),
                      );
                    },
                  })}
                />
              </Campo>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                id="pacienteCpf"
                rotulo="CPF"
                ajuda="Deixe em branco para remover."
                erro={formulario.formState.errors.pacienteCpf?.message}
              >
                <Entrada
                  inputMode="numeric"
                  placeholder="000.000.000-00"
                  {...formulario.register('pacienteCpf', {
                    onChange: (evento: React.ChangeEvent<HTMLInputElement>) => {
                      formulario.setValue('pacienteCpf', mascararCpf(evento.target.value));
                    },
                  })}
                />
              </Campo>

              <Campo
                id="pacienteCns"
                rotulo="Cartão Nacional de Saúde"
                ajuda="Deixe em branco para remover."
                erro={formulario.formState.errors.pacienteCns?.message}
              >
                <Entrada
                  inputMode="numeric"
                  placeholder="000 0000 0000 0000"
                  {...formulario.register('pacienteCns', {
                    onChange: (evento: React.ChangeEvent<HTMLInputElement>) => {
                      formulario.setValue('pacienteCns', mascararCns(evento.target.value));
                    },
                  })}
                />
              </Campo>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                id="pacienteTelefone"
                rotulo="Telefone"
                obrigatorio
                ajuda="Confirme o número com o paciente — é por ele que o agendamento é avisado."
                erro={formulario.formState.errors.pacienteTelefone?.message}
              >
                <Entrada
                  inputMode="tel"
                  placeholder="(00) 90000-0000"
                  {...formulario.register('pacienteTelefone', {
                    onChange: (evento: React.ChangeEvent<HTMLInputElement>) => {
                      formulario.setValue(
                        'pacienteTelefone',
                        mascararTelefone(evento.target.value),
                      );
                    },
                  })}
                />
              </Campo>

              <Campo
                id="pacienteEmail"
                rotulo="E-mail"
                ajuda="Recebe o aviso de agendamento."
                erro={formulario.formState.errors.pacienteEmail?.message}
              >
                <Entrada type="email" {...formulario.register('pacienteEmail')} />
              </Campo>
            </div>

            <Campo
              id="pacienteEndereco"
              rotulo="Endereço"
              ajuda="Gravado em caixa alta e sem acentuação."
            >
              <Entrada
                {...formulario.register('pacienteEndereco', {
                  onChange: (evento: React.ChangeEvent<HTMLInputElement>) => {
                    formulario.setValue(
                      'pacienteEndereco',
                      paraAsciiMaiusculo(evento.target.value),
                    );
                  },
                })}
              />
            </Campo>
          </CartaoConteudo>
        </Cartao>

        <Cartao>
          <CartaoCabecalho>
            <CartaoTitulo>Solicitação</CartaoTitulo>
          </CartaoCabecalho>
          <CartaoConteudo className="flex flex-col gap-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                id="tipo"
                rotulo="Tipo"
                obrigatorio
                erro={formulario.formState.errors.tipo?.message}
              >
                <Selecao {...formulario.register('tipo')}>
                  {TIPOS_SOLICITACAO.map((tipo) => (
                    <option key={tipo} value={tipo}>
                      {ROTULOS_TIPO[tipo]}
                    </option>
                  ))}
                </Selecao>
              </Campo>

              <Campo
                id="especialidadeId"
                rotulo="Especialidade"
                obrigatorio
                ajuda="Corrija quando a requisição do médico pedir outra."
                erro={formulario.formState.errors.especialidadeId?.message}
              >
                <Selecao {...formulario.register('especialidadeId')}>
                  {opcoesEspecialidade.map((especialidade) => (
                    <option key={especialidade.id} value={especialidade.id}>
                      {especialidade.nome}
                    </option>
                  ))}
                </Selecao>
              </Campo>
            </div>

            <Campo
              id="procedimento"
              rotulo="Procedimento ou exame solicitado"
              ajuda="Descreva o que foi solicitado, quando aplicável."
            >
              <Entrada {...formulario.register('procedimento')} />
            </Campo>

            <Campo
              id="prioridade"
              rotulo="Prioridade"
              obrigatorio
              ajuda={DESCRICOES_PRIORIDADE[valores.prioridade ?? 'ELETIVA']}
            >
              <Selecao {...formulario.register('prioridade')}>
                {PRIORIDADES.map((prioridade) => (
                  <option key={prioridade} value={prioridade}>
                    {ROTULOS_PRIORIDADE[prioridade]}
                  </option>
                ))}
              </Selecao>
            </Campo>

            {ehDemandaReprimida(solicitacao.origem) && (
              <Campo
                id="ubsId"
                rotulo="UBS de origem"
                obrigatorio
                ajuda="Unidade que encaminhou o pedido originalmente. Mover a demanda muda quem a acompanha."
                erro={formulario.formState.errors.ubsId?.message}
              >
                <Selecao {...formulario.register('ubsId')}>
                  {opcoesUbs.map((unidade) => (
                    <option key={unidade.id} value={unidade.id}>
                      {unidade.nome}
                    </option>
                  ))}
                </Selecao>
              </Campo>
            )}

            {ehDemandaReprimida(solicitacao.origem) && (
              <Campo
                id="pedidoOriginalEm"
                rotulo="Data do pedido original"
                ajuda="É ela que define a posição na fila. Em branco, a espera volta a contar da data do cadastro."
                erro={formulario.formState.errors.pedidoOriginalEm?.message}
              >
                <Entrada type="date" max={hojeIso()} {...formulario.register('pedidoOriginalEm')} />
              </Campo>
            )}

            <Campo
              id="observacoes"
              rotulo="Observações"
              ajuda="Informações clínicas ou administrativas relevantes para a análise."
            >
              <AreaTexto rows={4} {...formulario.register('observacoes')} />
            </Campo>
          </CartaoConteudo>
        </Cartao>

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Botao variante="contorno" type="button" asChild>
            <Link to={`/solicitacoes/${solicitacao.id}`}>Cancelar</Link>
          </Botao>
          <Botao type="submit" disabled={salvar.isPending}>
            {salvar.isPending ? (
              <Loader2 className="animate-spin" aria-hidden="true" />
            ) : (
              <Save aria-hidden="true" />
            )}
            {salvar.isPending ? 'Salvando…' : 'Salvar alterações'}
          </Botao>
        </div>
      </form>
    </div>
  );
}

/** Tela de recusa: diz o motivo e oferece o caminho de volta. */
function Impedimento({
  titulo,
  detalhe,
  idSolicitacao,
  origem = 'UBS',
}: {
  titulo: string;
  detalhe: string;
  idSolicitacao?: string;
  /** Sem a solicitação em mãos, a lista de volta é a das solicitações. */
  origem?: OrigemSolicitacao;
}) {
  return (
    <div className="flex flex-col items-center gap-4 py-16 text-center">
      <AlertCircle className="text-silenciado-texto size-12" aria-hidden="true" />
      <h1 className="text-lg font-semibold">{titulo}</h1>
      <p className="text-silenciado-texto max-w-prose text-sm">{detalhe}</p>
      <Botao asChild variante="contorno">
        <Link to={idSolicitacao ? `/solicitacoes/${idSolicitacao}` : caminhoDaLista(origem)}>
          {idSolicitacao ? 'Voltar à solicitação' : 'Voltar à lista'}
        </Link>
      </Botao>
    </div>
  );
}
